import { Button, Divider, Modal, Spin, Radio, Form, InputNumber, Space, Typography } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { IExportPurchaseInvoiceReport } from "../../../../models/trademark/request";

interface IProps {
  id: string;
  title: string;
  visible: boolean;
  toggleVisible: (visible: boolean) => void;
  onSubmit?: (data: IExportPurchaseInvoiceReport) => any;
  loading: boolean;
  width?: string;
}

type QuantityMode = "specific" | "range" | "less_than" | "more_than";

interface IQuantityUnit {
  piece: { min?: number; max?: number };
  kilogram: { min?: number; max?: number };
  ounce: { min?: number; max?: number };
}

function PurchaseInvoiceReportModal(props: IProps) {
  const { id, title, toggleVisible, visible, loading } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [quantityMode, setQuantityMode] = useState<QuantityMode | undefined>(undefined);
  const [quantities, setQuantities] = useState<IQuantityUnit>({
    piece: {},
    kilogram: {},
    ounce: {}
  });

  const handleQuantityChange = (unit: keyof IQuantityUnit, type: 'min' | 'max', value: number | null) => {
    setQuantities(prev => ({
      ...prev,
      [unit]: {
        ...prev[unit],
        [type]: value || 0
      }
    }));
  };

  const onSubmit = async () => {
    try {
      if (!quantityMode) {
        return;
      }

      let exportData: IExportPurchaseInvoiceReport;

      switch (quantityMode) {
        case "specific":
          exportData = {
            piece_min_quantity: quantities.piece.min || 0,
            piece_max_quantity: quantities.piece.min || 0, // Same as min for specific
            kilogram_min_quantity: quantities.kilogram.min || 0,
            kilogram_max_quantity: quantities.kilogram.min || 0,
            ounce_min_quantity: quantities.ounce.min || 0,
            ounce_max_quantity: quantities.ounce.min || 0,
          };
          break;
        case "range":
          exportData = {
            piece_min_quantity: quantities.piece.min || 0,
            piece_max_quantity: quantities.piece.max || 0,
            kilogram_min_quantity: quantities.kilogram.min || 0,
            kilogram_max_quantity: quantities.kilogram.max || 0,
            ounce_min_quantity: quantities.ounce.min || 0,
            ounce_max_quantity: quantities.ounce.max || 0,
          };
          break;
        case "less_than":
          exportData = {
            piece_min_quantity: 0,
            piece_max_quantity: quantities.piece.max || 0,
            kilogram_min_quantity: 0,
            kilogram_max_quantity: quantities.kilogram.max || 0,
            ounce_min_quantity: 0,
            ounce_max_quantity: quantities.ounce.max || 0,
          };
          break;
        case "more_than":
          exportData = {
            piece_min_quantity: quantities.piece.min || 0,
            piece_max_quantity: 999999, // Large number for "more than"
            kilogram_min_quantity: quantities.kilogram.min || 0,
            kilogram_max_quantity: 999999,
            ounce_min_quantity: quantities.ounce.min || 0,
            ounce_max_quantity: 999999,
          };
          break;
        default:
          exportData = {
            piece_min_quantity: 0,
            piece_max_quantity: 0,
            kilogram_min_quantity: 0,
            kilogram_max_quantity: 0,
            ounce_min_quantity: 0,
            ounce_max_quantity: 0,
          };
      }

      await props.onSubmit?.(exportData);
      toggleVisible(false);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const resetForm = () => {
    setQuantityMode(undefined);
    setQuantities({
      piece: {},
      kilogram: {},
      ounce: {}
    });
    form.resetFields();
  };

  const renderQuantityInputs = () => {
    const units: Array<{ key: keyof IQuantityUnit; label: string }> = [
      { key: "piece", label: t("piece") },
      { key: "kilogram", label: t("kilogram") },
      { key: "ounce", label: t("ounce") }
    ];

    return units.map(unit => (
      <div key={unit.key} style={{ marginBottom: 16 }}>
        <Typography.Text strong>{unit.label}</Typography.Text>
        <div style={{ marginTop: 8 }}>
          {(quantityMode === "specific" || quantityMode === "more_than") && (
            <Form.Item
              label={quantityMode === "specific" ? t("quantity") : t("minimum")}
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                min={0}
                value={quantities[unit.key].min}
                onChange={(value) => handleQuantityChange(unit.key, 'min', value)}
                style={{ width: "100%" }}
                placeholder={t("enter_quantity")}
              />
            </Form.Item>
          )}

          {(quantityMode === "range") && (
            <>
              <Form.Item
                label={t("minimum")}
                style={{ marginBottom: 8 }}
              >
                <InputNumber
                  min={0}
                  value={quantities[unit.key].min}
                  onChange={(value) => handleQuantityChange(unit.key, 'min', value)}
                  style={{ width: "100%" }}
                  placeholder={t("minimum_quantity")}
                />
              </Form.Item>
              <Form.Item
                label={t("maximum")}
                style={{ marginBottom: 8 }}
              >
                <InputNumber
                  min={quantities[unit.key].min || 0}
                  value={quantities[unit.key].max}
                  onChange={(value) => handleQuantityChange(unit.key, 'max', value)}
                  style={{ width: "100%" }}
                  placeholder={t("maximum_quantity")}
                />
              </Form.Item>
            </>
          )}

          {quantityMode === "less_than" && (
            <Form.Item
              label={t("maximum")}
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                min={0}
                value={quantities[unit.key].max}
                onChange={(value) => handleQuantityChange(unit.key, 'max', value)}
                style={{ width: "100%" }}
                placeholder={t("maximum_quantity")}
              />
            </Form.Item>
          )}
        </div>
      </div>
    ));
  };

  return (
    <Modal
      title={title}
      open={visible}
      closable
      destroyOnClose
      onCancel={() => {
        resetForm();
        toggleVisible(false);
      }}
      afterClose={() => {
        resetForm();
        toggleVisible(false);
      }}
      width={props.width || "60vw"}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={() => {
              resetForm();
              toggleVisible(false);
            }}
            style={{ marginInlineEnd: 8 }}
          >
            {t("cancel")}
          </Button>
          <Button
            loading={loading}
            onClick={onSubmit}
            type="primary"
          >
            {t("export")}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Divider style={{ margin: "10px 0 20px 0" }} />

          <Form.Item label={t("quantity_selection_mode")}>
            <Radio.Group
              value={quantityMode}
              onChange={(e) => {
                setQuantityMode(e.target.value);
                setQuantities({
                  piece: {},
                  kilogram: {},
                  ounce: {}
                });
              }}
            >
              <Space direction="vertical">
                <Radio value="specific">{t("specific_amount")}</Radio>
                <Radio value="range">{t("quantity_range")}</Radio>
                <Radio value="less_than">{t("less_than")}</Radio>
                <Radio value="more_than">{t("more_than")}</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {quantityMode && (
            <>
              <Divider />

              <Typography.Title level={5}>{t("quantity_parameters")}</Typography.Title>

              {renderQuantityInputs()}
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  );
}

export default PurchaseInvoiceReportModal;